import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { API_BASE } from '../services/api';
import './PaymentModal.css';
import PayPalButton from './PayPalButton';

function PaymentModal({ orderData, onClose, onSuccess }) {
  const [error, setError] = useState(null);
  const [isAccepted, setIsAccepted] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [displayPrice, setDisplayPrice] = useState(orderData.price);
  const [promoMessage, setPromoMessage] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const termsUrl = '/legal/terms';
  const privacyUrl = '/legal/privacy';
  const dpaUrl = '/legal/refunds';

  useEffect(() => {
    setDisplayPrice(orderData.price);
    setPromoCode('');
    setAppliedPromoCode('');
    setPromoMessage(null);
    setError(null);
  }, [orderData]);

  const applyPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) {
      setPromoMessage({ type: 'error', text: 'Enter a promo code first.' });
      setAppliedPromoCode('');
      setDisplayPrice(orderData.price);
      return;
    }

    setPromoLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/api/orders/price-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: orderData.domain,
          email: orderData.email || undefined,
          lead_id: orderData.leadId || undefined,
          price: orderData.price,
          package_type: orderData.packageType || 'domain_only',
          offer_token: orderData.offerToken || undefined,
          promo_code: code,
        }),
      });

      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.detail || data.error || 'Failed to preview promo pricing');
      }

      setDisplayPrice(Number(data.price || orderData.price));
      if (data.promo_applied) {
        setAppliedPromoCode(code);
        setPromoMessage({ type: 'success', text: data.promo_reason || 'Promo code applied.' });
      } else {
        setAppliedPromoCode('');
        setPromoMessage({ type: 'error', text: data.promo_reason || 'Promo code not applied.' });
      }
    } catch (err) {
      setAppliedPromoCode('');
      setDisplayPrice(orderData.price);
      setPromoMessage({ type: 'error', text: err.message || 'Promo code preview failed.' });
    } finally {
      setPromoLoading(false);
    }
  };

  const payableOrderData = {
    ...orderData,
    price: displayPrice,
    promoCode: appliedPromoCode || undefined,
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="payment-modal-overlay" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="payment-modal-backdrop" />
        </Transition.Child>

        <div className="payment-modal-container">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="payment-modal-panel">
              <Dialog.Title className="payment-modal-title">
                Secure Payment
              </Dialog.Title>

              <div className="payment-modal-content">
                <div className="order-summary">
                  <h3>Order Summary</h3>
                  <div className="summary-item">
                    <span>Domain:</span>
                    <strong>{orderData.domain}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Price:</span>
                    <strong>${displayPrice.toFixed(2)}</strong>
                  </div>
                </div>

                <div className="promo-box">
                  <label htmlFor="promoCode" className="promo-label">Promo Code</label>
                  <div className="promo-row">
                    <input
                      id="promoCode"
                      type="text"
                      className="promo-input"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Promo code (optional)"
                    />
                    <button type="button" className="promo-apply" onClick={applyPromoCode} disabled={promoLoading}>
                      {promoLoading ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                  {promoMessage && (
                    <div className={`promo-message is-${promoMessage.type}`}>{promoMessage.text}</div>
                  )}
                </div>

                {error && (
                  <div className="error-message">{error}</div>
                )}

                <div className="payment-legal-acceptance">
                  <label className="acceptance-checkbox">
                    <input
                      type="checkbox"
                      checked={isAccepted}
                      onChange={(e) => setIsAccepted(e.target.checked)}
                    />
                    <span className="acceptance-text">
                      I accept the <a href={termsUrl} target="_blank" rel="noopener noreferrer">Terms of Service</a>,
                      <a href={privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a>,
                      and the <a href={dpaUrl} target="_blank" rel="noopener noreferrer">Refund Policy</a>.
                    </span>
                  </label>
                </div>

                <div className={`payment-actions ${!isAccepted ? 'disabled' : ''}`}>
                  {isAccepted ? (
                    <PayPalButton
                      orderData={payableOrderData}
                      onSuccess={onSuccess}
                      onError={setError}
                    />
                  ) : (
                    <button className="pay-placeholder" disabled>
                      Please accept terms to continue
                    </button>
                  )}
                </div>

                <div className="payment-security">
                  <p>
                    <span aria-hidden="true"><i className="fa-solid fa-lock" aria-hidden="true" /></span> Secure payment processing
                  </p>
                  <p>
                    <span aria-hidden="true"><i className="fa-solid fa-circle-check" aria-hidden="true" /></span> 7-day money-back guarantee
                  </p>
                </div>
              </div>

              <button type="button" className="payment-modal-close" onClick={onClose} aria-label="Close" title="Close">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default PaymentModal;
